
import {from as observableFrom,  throwError as observableThrowError, Observable } from 'rxjs';

import {mergeMap,  catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpHeaders
} from '@angular/common/http';
import { AuthTrackingService } from '@core/services/authTracking.service';
import { ApiService } from '@core/services/api.service';

import { ApiRoutes } from '@shared/models/configs/api.endpoints';

import { environment } from '@environments/environment';
import { ConfirmModalService } from '@core/services/confirm-modal.service';

import {
  ErrorCodesService,
  ProxyService,
  SignOutService,
  OverlayService,
  DetectBrowser
} from '@core/services';
import { LoggingService } from '@core/services/logging.service';
import { FeatureFlagService } from '@core/services/statsig/feature-flag.service';
import { RefreshToken } from '@features/account/models/refresh-token.model';
import { NgRedux } from '@angular-redux/store';
import { IAppState, Actions, TokenInfo } from '@core/redux/stores';
import * as HttpStatus from 'http-status-codes';
import { Guid } from 'ez-guid';
import * as Fingerprint2 from 'fingerprintjs2';
import projectData from '../../project.json';

// the following are for mocking back end calls that aren't written yet
// TODO remove the backend mockup code

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
  private authToken: string;
  public deviceId: string;
  private uuid: Guid;
  private browserFingerPrint: string;
  private stableIdUpdated = false;

  constructor(
    private authTrackingService: AuthTrackingService,
    private apiService: ApiService,
    private confirmModalService: ConfirmModalService,
    private logger: LoggingService,
    private proxyService: ProxyService,
    private ngRedux: NgRedux<IAppState>,
    private signoutService: SignOutService,
    private overlayService: OverlayService,
    private detectBrowserService: DetectBrowser,
    private featureFlagService: FeatureFlagService
  ) {
    // Handling confirm dialog button click
    this.confirmModalService.btnEvent.subscribe((data: string) => {
      if (data === 'nonetworkBtn1') {
        logger.log('No Network');
      }
    });
    this.uuid = Guid.create();
  }

  private tryRefreshStatsigUser(): void {
    if (this.stableIdUpdated) {
      return;
    }
    const fingerprint = this.browserFingerPrint || localStorage.getItem('browserFingerPrint');
    if (!fingerprint) {
      return;
    }
    this.stableIdUpdated = true;
    this.featureFlagService.refreshUser();
  }

  private getAppVersionHeader(): string {
    const version = typeof projectData?.version === 'string' ? projectData.version : '';
    return version.match(/^\d+\.\d+\.\d+/)?.[0] ?? version;
  }

  getFingerprintAsync = (request: HttpRequest<any>) => new Promise(resolve => {
    if (this.browserFingerPrint) {
      this.tryRefreshStatsigUser();
      resolve(null);
    } else {
      this.browserFingerPrint = this.detectBrowserService.getDeviceFingerPrint();
      if (this.browserFingerPrint) {
        this.tryRefreshStatsigUser();
        resolve(null);
      } else {
        if (request.method && request.method.toUpperCase() !== 'OPTIONS') {
          Fingerprint2.getV18((result, components) => {
            this.browserFingerPrint = result;
            localStorage.setItem('browserFingerPrint', result);
            this.tryRefreshStatsigUser();
            resolve(null);
          });
        }
      }
    }
  })

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return observableFrom(this.getFingerprintAsync(request)).pipe(mergeMap((): Observable<any> => {
    let newUrl: string;
    let isCMS = false;

    let apiKey: string = this.apiService.getMobileApiKey();
    let deviceId = this.uuid.toString();
    const localDeviceId = localStorage.getItem('deviceId');
    localDeviceId ? deviceId = localDeviceId : localStorage.setItem('deviceId', deviceId);

    // First Check to see if internet connection is available, else continue with normal flow
    if (!window.navigator.onLine) {
      // notify user with no network confirm popup
      setTimeout(() => {
        this.openNoNetPop();
      }, 500);
      // return HttpErrorResponse error with temporary status code -100 to handle all http subscriptions in components and services
      return observableThrowError(
        new HttpErrorResponse({
          error: 'No Internet connection !',
          status: -100
        })
      );
    } else {
      // check if the call is for the mobile ordering/account web api or the web admin
      // content management system api. prepend the proper endpoint to the URL call
      if (request.url.indexOf('/cms/') > -1) {
        newUrl = this.apiService.getCmsAPI() + request.url;
        apiKey = this.apiService.getCmsAPIKey();
        isCMS = true;
      } else if (request.url.indexOf('http') > -1) {
        // do nothing as the request is explicit.
      } else {
        newUrl = this.apiService.getMobileAPI() + request.url;
      }

      const tracing = this.apiService.getVerboseTracing();
      if (this.authTrackingService.isLoggedIn()) {
        this.authToken = this.authTrackingService.getAuthToken();
        request = this.addHeadersForLoggedInUserRequest(
          request,
          newUrl,
          apiKey,
          deviceId,
          this.browserFingerPrint
        );
      } else {
        request = this.addHeadersForAnonymousUserRequest(
          request,
          newUrl,
          apiKey,
          deviceId,
          this.browserFingerPrint,
          isCMS
        );
      }

      /* Adding additional http header to include lat/long */
      // request = this.addLatLongInfo(request); // LatLong information passed as httpheader instead of body

      // if (tracing) {
      //   this.logger.log('');
      //   this.logger.log('*********************** HTTP REQUEST LOG START *********************');
      //   this.logger.log(('request.url = ' + request.url).trim());
      //   this.logger.log(('request.method = ' + request.method).trim());
      //   this.logger.log('request.headers = ', request.headers);
      //   this.logger.log('request.params = ', request.params);
      //   this.logger.log('request.body = ', request.body);
      //   this.logger.log('*********************** HTTP REQUEST LOG END *********************');
      //   this.logger.log('');
      // }
      return next.handle(request).pipe(
        catchError(event => {
          if (event instanceof HttpErrorResponse) {
            const response = event as HttpErrorResponse;
            if (
              response.status === HttpStatus.UNAUTHORIZED &&
              this.authTrackingService !== undefined &&
              this.authTrackingService.getRefreshToken()
            ) {
              const payload = new RefreshToken(
                this.authTrackingService.getRefreshToken()
              );
              this.logger.logEvent(
                'Access or Refresh token has expired, using refresh token to get a new access token and retry the request'
              );
              return this.proxyService
                .httpPost(ApiRoutes.REFRESHTOKEN, payload).pipe(
                mergeMap((success: any) => {
                  const tokenResp = <TokenInfo>success.body.valueOf();
                  this.ngRedux.dispatch({
                    type: Actions.USERTOKENREFRESHED,
                    data: tokenResp
                  });
                  this.authToken = tokenResp.accessToken;
                  request = this.addHeadersForLoggedInUserRequest(
                    request,
                    newUrl,
                    apiKey,
                    deviceId,
                    this.browserFingerPrint
                  );
                  request = this.addLatLongInfo(request);
                  return next.handle(request);
                }));
            } else if (response.status === HttpStatus.BAD_REQUEST && response.error && response.error.code === 4003) {
              this.logger.logError(response.error);
              // Refresh token request failed - could be an invalid refresh token itself - logging out the user
              this.signOut();
            } else if (
              response.headers.get('content-type') === 'application/json'
            ) {
              const message = ErrorCodesService.getErrorMessage(
                event.error.code,
                event.error.message
              );
              const errorObj = JSON.parse(response.error);
              errorObj.message = message;
              return observableThrowError(
                new HttpErrorResponse({
                  error: errorObj,
                  headers: response.headers,
                  status: response.status,
                  statusText: response.statusText,
                  url: response.url
                })
              );
            }
          }
          return observableThrowError(event);
        })
      );
    }
  }));
  }
  private addLatLongInfo(request: HttpRequest<any>) {
    if (request.body) {
      const bodyStr = JSON.stringify(request.body); // To handle if any request body object is empty
      const bodyObj = JSON.parse(bodyStr);
      if (bodyObj['x-coordinates']) {
        request = request.clone({
          setHeaders: {
            'x-coordinates': bodyObj['x-coordinates']
          }
        });
      }
    }
    return request;
  }

  private addHeadersForAnonymousUserRequest(
    request: HttpRequest<any>,
    newUrl: string,
    apiKey: string,
    deviceId: string,
    browserFingerPrint: string,
    isCMS: boolean
  ) {
    const setHeaders = {
      'x-api-key': apiKey,
      'Content-Type': 'application/json',
      'cache-control': 'no-cache',
      accept: 'application/json',
      'x-device-id': deviceId,
      'x-device-fingerprint': browserFingerPrint ? browserFingerPrint : (this.detectBrowserService.getDeviceFingerPrint()
      ? this.detectBrowserService.getDeviceFingerPrint() : ''),
      'x-device-appversion': this.getAppVersionHeader(),
      'x-client': 'SPA',
      'x-device-platform': 'dotcom'
    };
    if (isCMS) {
      delete setHeaders['cache-control'];
    }
    request = request.clone({
      url: newUrl,
      body: request.body,
      params: request.params,
      setHeaders: setHeaders
    });
    if (request.method === 'POST' && request.body instanceof FormData) {
      request = request.clone({
        headers: request.headers.delete('Content-Type')
      });
    }
    request = this.addIdempotencyHeader(request, false);
    return request;
  }

  private addHeadersForLoggedInUserRequest(
    request: HttpRequest<any>,
    newUrl: string,
    apiKey: string,
    deviceId: string,
    browserFingerPrint: string
  ) {
    request = request.clone({
      url: newUrl,
      body: request.body,
      params: request.params,
      setHeaders: {
        Authorization: 'Bearer ' + this.authToken,
        'x-api-key': apiKey,
        'Content-Type': 'application/json',
        accept: 'application/json',
        'x-device-id': deviceId,
        'x-device-fingerprint': browserFingerPrint ? browserFingerPrint : (this.detectBrowserService.getDeviceFingerPrint()
        ? this.detectBrowserService.getDeviceFingerPrint() : ''),
        'x-device-appversion': this.getAppVersionHeader(),
        'x-client': 'SPA',
        'x-device-platform': 'dotcom'
      }
    });
    if (request.method === 'POST' && request.body instanceof FormData) {
      request = request.clone({
        headers: request.headers.delete('Content-Type')
      });
    }
    request = this.addIdempotencyHeader(request, true);
    return request;
  }

  private addIdempotencyHeader(request: HttpRequest<any>, signedIn = true) {
    const orderMethodName = signedIn ? 'submit' : 'guestusercheckout';
    if (request.url.indexOf(orderMethodName) > -1) {
      if (sessionStorage.getItem('idempotency_key')) {
        request = request.clone({
          setHeaders: {
            'idempotency-key': sessionStorage.getItem('idempotency_key') ? sessionStorage.getItem('idempotency_key') : ''
          }
        });
      }
    }
    return request;
  }

  openNoNetPop() {
    setTimeout(() => {
      if (
        !this.confirmModalService['trackModalService']['isConfirmModalOpen']
      ) {
        this.confirmModalService.show(
          'No network connection',
          '',
          'Hmm, we lost the connection there for a second. Please try again later',
          'OK, Got It',
          '',
          '',
          true,
          'nonetworkBtn1'
        );
      }
    }, 500);
  }
  signOut() {
    this.ngRedux.dispatch({ type: Actions.USERLOGGEDOUT });
    sessionStorage.removeItem('accountBalance');
    this.overlayService.displayOverlay(
      'Thank You! <br> Please come back soon.',
      '/home'
    );
    this.signoutService.logOut();
  }
}
